import Keycloak from 'keycloak-js';
import { reactive, readonly } from 'vue';
import { config } from '@/config';

const STORAGE_KEY = 'hypcheck.auth';

const keycloak = new Keycloak({
  url: config.keycloakUrl,
  realm: config.keycloakRealm,
  clientId: config.keycloakClientId,
});

const state = reactive({
  initialized: false,
  loading: false,
  authenticated: false,
  profile: null,
  roles: [],
  accessError: null,
});

let refreshTimer = null;
let initPromise = null;
let updatePromise = null;

function loadStoredTokens() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function persistTokens() {
  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      token: keycloak.token,
      refreshToken: keycloak.refreshToken,
    }),
  );
}

function clearStorage() {
  sessionStorage.removeItem(STORAGE_KEY);
}

function scheduleRefresh() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
  }
  if (!state.authenticated || !keycloak.tokenParsed) {
    return;
  }
  const expiresIn =
    keycloak.tokenParsed.exp * 1000 - Date.now();
  const refreshIn = Math.max(expiresIn - 60000, 15000);
  refreshTimer = setTimeout(() => {
    executeTokenUpdate(60).catch(() => {
      // handled inside executeTokenUpdate (logout)
    });
  }, refreshIn);
}

function backendAccessMessage(errorPayload) {
  if (errorPayload && typeof errorPayload.detail === 'string') {
    return errorPayload.detail;
  }
  return 'Ihr HypCheck-Zugang ist nicht aktiv.';
}

async function validateBackendAccess() {
  const apiBaseUrl = config.apiUrl || '';
  const response = await fetch(`${apiBaseUrl}/auth/me`, {
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
    },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(backendAccessMessage(payload));
  }
  return payload;
}

function clearAuthenticatedState(accessError = null) {
  state.authenticated = false;
  state.profile = null;
  state.roles = [];
  state.accessError = accessError;
  clearStorage();
  if (typeof keycloak.clearToken === 'function') {
    keycloak.clearToken();
  }
}

async function executeTokenUpdate(minValidity = 60) {
  if (!state.authenticated) {
    return;
  }
  if (updatePromise) {
    return updatePromise;
  }
  updatePromise = keycloak
    .updateToken(minValidity)
    .then(() => {
      persistTokens();
      scheduleRefresh();
    })
    .catch(async (error) => {
      console.error('Token refresh failed', error);
      await logout();
      throw error;
    })
    .finally(() => {
      updatePromise = null;
    });
  return updatePromise;
}

async function init() {
  if (state.initialized) {
    return state.authenticated;
  }
  if (initPromise) {
    return initPromise;
  }
  state.loading = true;
  const stored = loadStoredTokens();

  initPromise = keycloak
    .init({
      onLoad: 'check-sso',
      checkLoginIframe: false,
      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      token: stored?.token,
      refreshToken: stored?.refreshToken,
    })
    .then(async (authenticated) => {
      state.authenticated = authenticated;
      state.accessError = null;
      if (authenticated) {
        try {
          state.profile = await keycloak.loadUserProfile();
        } catch {
          state.profile = null;
        }
        state.roles = keycloak.tokenParsed?.realm_access?.roles || [];
        try {
          await validateBackendAccess();
        } catch (error) {
          clearAuthenticatedState(error.message);
          return false;
        }
        persistTokens();
        scheduleRefresh();
      } else {
        clearAuthenticatedState();
      }
      return state.authenticated;
    })
    .finally(() => {
      state.initialized = true;
      state.loading = false;
    });

  return initPromise;
}

async function ensureAuthenticated() {
  const authenticated = await init();
  if (!authenticated) {
    throw new Error('User is not authenticated');
  }
}

async function getValidToken() {
  await ensureAuthenticated();
  await executeTokenUpdate(30);
  return keycloak.token;
}

function login(redirectPath = '/', options = {}) {
  const loginOptions = {
    locale: options.locale || 'de',
    redirectUri: `${window.location.origin}${redirectPath}`,
  };
  if (options.loginHint) {
    loginOptions.loginHint = options.loginHint;
  }
  if (options.scope) {
    loginOptions.scope = options.scope;
  }
  keycloak.login(loginOptions);
}

async function logout() {
  clearStorage();
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  state.authenticated = false;
  state.profile = null;
  try {
    await keycloak.logout({
      redirectUri: `${window.location.origin}/login`,
    });
  } catch (error) {
    console.error('Keycloak logout failed', error);
  }
}

function hasRealmRole(role) {
  return state.roles.includes(role);
}

export default {
  state: readonly(state),
  init,
  ensureAuthenticated,
  getValidToken,
  login,
  logout,
  hasRealmRole,
};
